import React, { useState, useEffect, useRef } from 'react';
import { opcionesDeporte } from '../../lib/deportesOrg';
import { Save, Upload, X, Building2 } from 'lucide-react';
import { FeaturedLayout } from '../layouts';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../hooks/useAuth';
import { updateOrganization, uploadOrganizationLogo, getOrgDeportes, getCurrentPlan, getCurrentUsage, getPlanLimits, renderAvisoClub, getExpedienteLegal, renderLegalDoc, renderDocRequerido, LEGAL_DOC_KEYS } from '../../lib/supabase';
import { usePlatformLegal } from '../../hooks/usePlatformLegal';
import LegalDocLink from '../legal/LegalDocLink';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import ComparticionDatos from './ComparticionDatos';
import { ColorPicker } from '../ui/color-picker';
import PlanBadge from '../ui/PlanBadge';
import UsageLimitWarning from '../ui/UsageLimitWarning';
import { PAISES, ESTADOS_MX, PAIS_DEFAULT } from '../../constants/ubicacion';
import { camposLegalesFaltantes } from '../../lib/consent';

const COLOR_PRESETS = [
  { name: 'Negro', primary: '#000000', secondary: '#B5B8BE' },
  { name: 'Azul Marino', primary: '#1E3A5F', secondary: '#A3C4DC' },
  { name: 'Rojo', primary: '#8B0000', secondary: '#E8C4C4' },
  { name: 'Verde', primary: '#1B4332', secondary: '#95D5B2' },
  { name: 'Morado', primary: '#4A1A6B', secondary: '#C4A7D7' },
  { name: 'Naranja', primary: '#7C2D12', secondary: '#FED7AA' },
];

// Que agrego al DPA cada enmienda vieja por casilla (hasta la pieza 2b; ver getExpedienteLegal).
// Las anteriores a `clausula_nueva` no lo dicen.
const CLAUSULA_ENMIENDA = { curp: 'cláusula de la CURP', foto: 'cláusula de la foto' };

const OrganizationSettings = () => {
  const { org, orgLogo, refreshOrg } = useOrganization();
  const { staff } = useAuth();
  const { legal: legalPlataforma } = usePlatformLegal();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    short_name: '',
    color_primary: '#000000',
    color_secondary: '#B5B8BE',
    color_background: '#F2F2F3',
    color_icon_background: '#000000',
    pais: PAIS_DEFAULT,
    estado: '',
    ciudad: '',
    razon_social: '',
    domicilio_fiscal: '',
    correo_privacidad: '',
    exigir_consentimiento: false,
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [orgDeportes, setOrgDeportes] = useState([]);
  const [expediente, setExpediente] = useState([]);
  const [cargandoExpediente, setCargandoExpediente] = useState(true);
  const [planCode, setPlanCode] = useState('starter');
  const [usage, setUsage] = useState({ players: 0, groups: 0, staff: 0, deportes: 0 });
  const [limits, setLimits] = useState({ players_max: 0, groups_max: 0, staff_max: 0, deportes_max: 0 });

  useEffect(() => {
    if (org) {
      setForm({
        name: org.name || '',
        short_name: org.short_name || '',
        color_primary: org.color_primary || '#000000',
        color_secondary: org.color_secondary || '#B5B8BE',
        color_background: org.color_background || '#F2F2F3',
        color_icon_background: org.color_icon_background || org.color_primary || '#000000',
        pais: org.pais || PAIS_DEFAULT,
        estado: org.estado || '',
        ciudad: org.ciudad || '',
        razon_social: org.razon_social || '',
        domicilio_fiscal: org.domicilio_fiscal || '',
        correo_privacidad: org.correo_privacidad || '',
        exigir_consentimiento: org.exigir_consentimiento || false,
      });
      getOrgDeportes(org.id).then(({ data }) => {
        if (data) setOrgDeportes(data);
      });
      setCargandoExpediente(true);
      getExpedienteLegal(org.id).then(({ data }) => {
        setExpediente(data || []);
        setCargandoExpediente(false);
      });
    }
  }, [org]);

  // El Aviso de Privacidad se arma sustituyendo estos tres datos. Sin ellos, el
  // tutor firmaria un aviso que no dice quien es el responsable ni donde ejercer
  // sus derechos ARCO -- que es justo lo que la ley exige que aparezca. Por eso
  // el interruptor no se puede ENCENDER sin ellos (apagarlo siempre se puede).
  // La lista vive en lib/consent para que el alta de la org y el envio de
  // solicitudes de firma exijan EXACTAMENTE lo mismo que esta pantalla.
  const faltanLegales = camposLegalesFaltantes(form)
  const puedeExigirConsentimiento = faltanLegales.length === 0

  // Los documentos de plataforma se arman con la entidad legal de MYKLUBB, para que el enlace
  // muestre EXACTAMENTE el texto vigente. Desde la pieza 2b el DPA es el mismo para todos los
  // clubes: ya no depende de casillas.
  const docsPlataforma = {}
  ;(expediente || []).forEach((d) => {
    docsPlataforma[d.docKey] = renderDocRequerido(d.docKey, legalPlataforma || {})
  })

  // El Aviso que firman los tutores, armado con los datos de esta pantalla.
  const avisoClub = renderAvisoClub({
    name: form.name,
    razon_social: form.razon_social,
    domicilio_fiscal: form.domicilio_fiscal,
    correo_privacidad: form.correo_privacidad,
  })

  const fechaLegible = (iso) => {
    if (!iso) return '—'
    try {
      return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return '—'
    }
  }


  useEffect(() => {
    let cancelled = false;
    const loadBillingInfo = async () => {
      const orgId = org?.id;
      if (!orgId) return;
      const [plan, usageRes, limitsRes] = await Promise.all([
        getCurrentPlan(orgId),
        getCurrentUsage(orgId),
        getPlanLimits(orgId),
      ]);
      if (cancelled) return;
      setPlanCode(plan || 'starter');
      if (usageRes?.data) setUsage(usageRes.data);
      if (limitsRes) setLimits(limitsRes);
    };
    loadBillingInfo();
    return () => { cancelled = true; };
  }, [org?.id]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'El logo no debe superar 2 MB' });
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!org?.id) return;

    setSaving(true);
    setMessage(null);

    try {
      if (logoFile) {
        const { error: logoError } = await uploadOrganizationLogo(org.id, logoFile);
        if (logoError) throw logoError;
      }

      const { error } = await updateOrganization(org.id, {
        name: form.name.trim(),
        short_name: form.short_name.trim() || null,
        color_primary: form.color_primary,
        color_secondary: form.color_secondary,
        color_background: form.color_background,
        color_icon_background: form.color_icon_background,
        pais: form.pais || null,
        estado: form.estado.trim() || null,
        ciudad: form.ciudad.trim() || null,
        razon_social: form.razon_social.trim() || null,
        domicilio_fiscal: form.domicilio_fiscal.trim() || null,
        correo_privacidad: form.correo_privacidad.trim() || null,
        exigir_consentimiento: form.exigir_consentimiento,
      });

      if (error) throw error;

      // Releer la org: el contexto la cargaba una sola vez y se quedaba con el
      // estado del arranque. El formulario se repuebla desde ahi, asi que tras
      // guardar mostraba los campos como estaban ANTES -- parecia que no se habia
      // guardado, y volver a darle a Guardar mandaba esos vacios de regreso.
      await refreshOrg();

      setMessage({ type: 'success', text: 'Configuracion guardada. Recarga la pagina para ver los cambios de colores.' });
      setLogoFile(null);
      setLogoPreview(null);
    } catch (err) {
      console.error('Error saving organization:', err);
      setMessage({ type: 'error', text: `Error al guardar: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (preset) => {
    setForm(prev => ({
      ...prev,
      color_primary: preset.primary,
      color_secondary: preset.secondary,
    }));
  };

  if (!org) {
    return (
      <FeaturedLayout title="Configuración">
        <div className="p-6 text-center text-muted-foreground">Cargando...</div>
      </FeaturedLayout>
    );
  }

  return (
    <FeaturedLayout title="Configuración de la organización">
      <div className="max-w-2xl mx-auto flex flex-col gap-6">

        {message && (
          <div className={`rounded-xl p-4 text-sm font-medium ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.text}
          </div>
        )}

        {/* Plan y uso */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="text-foreground font-semibold text-lg">Plan y uso</div>
            <PlanBadge plan={planCode} />
          </div>
          <div className="flex flex-col gap-3">
            <UsageLimitWarning resource="Jugadores activos" current={usage.players} max={limits.players_max} />
            <UsageLimitWarning resource="Grupos" current={usage.groups} max={limits.groups_max} />
            <UsageLimitWarning resource="Staff" current={usage.staff} max={limits.staff_max} />
            <UsageLimitWarning resource="Deportes activos" current={usage.deportes} max={limits.deportes_max} />
          </div>
        </div>

        {/* Identidad */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2 text-foreground font-semibold text-lg">
            <Building2 />
            Identidad
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="org_name">Nombre de la organización</Label>
              <Input
                id="org_name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ej: Raiders TRC"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="org_short_name">Nombre corto</Label>
              <Input
                id="org_short_name"
                value={form.short_name}
                onChange={(e) => handleChange('short_name', e.target.value)}
                placeholder="Ej: Raiders"
                maxLength={50}
              />
              <p className="text-xs text-muted-foreground">Se muestra en el sidebar y menu</p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="org_pais">País</Label>
              <select
                id="org_pais"
                value={form.pais}
                onChange={(e) => handleChange('pais', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {PAISES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="org_estado">Estado</Label>
              {form.pais === 'México' ? (
                <select
                  id="org_estado"
                  value={form.estado}
                  onChange={(e) => handleChange('estado', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Selecciona un estado</option>
                  {ESTADOS_MX.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              ) : (
                <Input
                  id="org_estado"
                  value={form.estado}
                  onChange={(e) => handleChange('estado', e.target.value)}
                  placeholder="Estado / provincia"
                />
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="org_ciudad">Ciudad</Label>
              <Input
                id="org_ciudad"
                value={form.ciudad}
                onChange={(e) => handleChange('ciudad', e.target.value)}
                placeholder="Ej: Guadalajara"
              />
            </div>
          </div>
        </div>

        {/* Datos legales (Aviso de Privacidad) */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div>
            <div className="text-foreground font-semibold text-lg">Datos legales</div>
            <p className="text-xs text-muted-foreground mt-1">
              Se usan para completar el Aviso de Privacidad que ve el tutor al registrar a un jugador.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label htmlFor="org_razon_social">Razón social</Label>
              <Input
                id="org_razon_social"
                value={form.razon_social}
                onChange={(e) => handleChange('razon_social', e.target.value)}
                placeholder="Nombre legal completo del club"
              />
            </div>

            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label htmlFor="org_domicilio_fiscal">Domicilio</Label>
              <Input
                id="org_domicilio_fiscal"
                value={form.domicilio_fiscal}
                onChange={(e) => handleChange('domicilio_fiscal', e.target.value)}
                placeholder="Domicilio del responsable"
              />
            </div>

            <div className="flex flex-col gap-1.5 md:col-span-2">
              <Label htmlFor="org_correo_privacidad">Correo para derechos ARCO</Label>
              <Input
                id="org_correo_privacidad"
                type="email"
                value={form.correo_privacidad}
                onChange={(e) => handleChange('correo_privacidad', e.target.value)}
                placeholder="privacidad@tuclub.com"
              />
              <p className="text-xs text-muted-foreground">
                Correo donde los tutores ejercen sus derechos de Acceso, Rectificación, Cancelación y Oposición.
              </p>
            </div>
          </div>

          <div className="pt-1">
            <LegalDocLink
              doc={avisoClub}
              label="Ver Aviso de Privacidad del club →"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Versión {avisoClub?.version} vigente. Se arma con los datos de arriba: cámbialos y cambia el
              documento que firman los tutores.
            </p>
          </div>

          <div className="pt-2 border-t space-y-2">
            <label className={`flex items-start gap-3 ${puedeExigirConsentimiento || form.exigir_consentimiento ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
              <input
                type="checkbox"
                className="h-4 w-4 mt-0.5 rounded border-input"
                checked={form.exigir_consentimiento}
                disabled={!puedeExigirConsentimiento && !form.exigir_consentimiento}
                onChange={(e) => handleChange('exigir_consentimiento', e.target.checked)}
              />
              <span className="text-sm text-muted-foreground">
                <span className="text-foreground font-medium">Exigir consentimiento del tutor para activar jugadores.</span>{' '}
                Mientras esté apagado puedes registrar y activar jugadores sin consentimiento;
                el sistema igual lleva la cuenta de quién ha firmado. Al encenderlo,{' '}
                <strong>no se desactiva a nadie</strong>: solo se exige la firma para las
                activaciones de ahí en adelante.
              </span>
            </label>

            {!puedeExigirConsentimiento && !form.exigir_consentimiento && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Para poder exigirlo, primero completa {faltanLegales.join(', ')}. El Aviso de
                Privacidad que firma el tutor se arma con esos datos, y sin ellos saldría sin
                decir quién es el responsable ni dónde ejercer sus derechos.
              </p>
            )}

            {!puedeExigirConsentimiento && form.exigir_consentimiento && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Falta {faltanLegales.join(', ')}. Estás exigiendo consentimiento con un Aviso de
                Privacidad incompleto: complétalo cuanto antes.
              </p>
            )}
          </div>
        </div>

        {/* Expediente legal. El sistema guardaba quién aceptó y cuándo desde el
            principio y no había dónde verlo: un club que firma un contrato tiene
            derecho a consultar qué firmó sin pedírselo a nadie. */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div>
            <div className="text-foreground font-semibold text-lg">Documentos legales</div>
            <p className="text-xs text-muted-foreground mt-1">
              Lo que tu club aceptó con MYKLUBB. Solo el titular de la cuenta puede aceptarlos.
            </p>
          </div>

          {cargandoExpediente ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {expediente.map((d) => (
                <li key={d.docKey} className="rounded-md border px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium">{d.titulo}</span>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-[10px]">v{d.versionVigente}</Badge>
                      {docsPlataforma[d.docKey] && (
                        <LegalDocLink
                          doc={docsPlataforma[d.docKey]}
                          label="Leer →"
                          className="text-sm text-primary underline hover:opacity-80"
                        />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {d.alDia ? (
                      <>
                        Aceptado el <strong className="text-foreground">{fechaLegible(d.aceptadoEl)}</strong>
                        {d.aceptadoPor && <> por <strong className="text-foreground">{d.aceptadoPor}</strong></>}
                      </>
                    ) : d.versionAceptada ? (
                      <span className="text-amber-700">
                        Se aceptó la versión {d.versionAceptada}. Falta que el titular de la cuenta
                        acepte la {d.versionVigente}.
                      </span>
                    ) : (
                      <span className="text-amber-700">
                        Sin aceptar. El titular de la cuenta debe revisarlo y aceptarlo.
                      </span>
                    )}
                  </p>
                  {d.invalidada && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Hubo una aceptación anulada{d.invalidada.quien && <> de {d.invalidada.quien}</>}
                      {d.invalidada.motivo && <>: {d.invalidada.motivo}</>}
                    </p>
                  )}
                  {/* Enmiendas viejas por casilla (antes de la pieza 2b): siguen en el registro, aparte. */}
                  {d.enmiendas?.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1.5">
                      <span>Enmiendas:</span>
                      <ul className="mt-0.5 space-y-0.5">
                        {d.enmiendas.map((e, i) => (
                          <li key={i}>
                            {fechaLegible(e.fecha)} · {CLAUSULA_ENMIENDA[e.clausula] || 'compartición de datos'}
                            {e.quien && <> · {e.quien}</>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="border-t pt-3">
            <p className="text-xs text-muted-foreground mb-2">Documentos que ven los tutores</p>
            <div className="flex flex-wrap gap-4 text-sm">
              <LegalDocLink
                doc={avisoClub}
                label="Aviso de Privacidad del club →"
                className="text-primary underline hover:opacity-80"
              />
              <LegalDocLink
                doc={renderLegalDoc(LEGAL_DOC_KEYS.CARTA_CONSENTIMIENTO, { '[NOMBRE DEL CLUB]': form.name })}
                label="Carta de consentimiento →"
                className="text-primary underline hover:opacity-80"
              />
            </div>
          </div>
        </div>

        <ComparticionDatos />

        {/* Logo */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="text-foreground font-semibold text-lg">Logo</div>

          <div className="flex items-center gap-6">
            <div className="size-20 bg-muted rounded-xl flex items-center justify-center overflow-hidden border">
              {logoPreview ? (
                <img src={logoPreview} alt="Preview" className="size-full object-contain" />
              ) : orgLogo ? (
                <img src={orgLogo} alt={org.name} className="size-full object-contain" />
              ) : (
                <Building2 size={32} className="text-muted-foreground" />
              )}
            </div>

            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/svg+xml,image/png,image/jpeg,image/webp"
                onChange={handleLogoSelect}
                className="hidden"
              />
              <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload data-icon="inline-start" />
                {logoPreview ? 'Cambiar' : 'Subir logo'}
              </Button>
              {logoPreview && (
                <Button variant="link" size="sm" className="text-destructive h-auto p-0 justify-start"
                  onClick={handleRemoveLogo}>
                  <X data-icon="inline-start" />
                  Cancelar
                </Button>
              )}
              <p className="text-xs text-muted-foreground">SVG, PNG o JPG. Max 2 MB.</p>
            </div>
          </div>
        </div>

        {/* Colores */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="text-foreground font-semibold text-lg">Colores</div>

          {/* Presets */}
          <div className="flex flex-col gap-2">
            <Label>Presets</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map((preset) => (
                <Button
                  key={preset.name}
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset(preset)}
                  className="gap-2"
                >
                  <div className="flex gap-1">
                    <span className="size-4 rounded-full ring-1 ring-inset ring-black/10" style={{ backgroundColor: preset.primary }} />
                    <span className="size-4 rounded-full ring-1 ring-inset ring-black/10" style={{ backgroundColor: preset.secondary }} />
                  </div>
                  {preset.name}
                </Button>
              ))}
            </div>
          </div>

          {/* Color pickers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <ColorPicker
              label="Primario"
              value={form.color_primary}
              onChange={(v) => handleChange('color_primary', v)}
            />
            <ColorPicker
              label="Secundario"
              value={form.color_secondary}
              onChange={(v) => handleChange('color_secondary', v)}
            />
            <ColorPicker
              label="Fondo"
              value={form.color_background}
              onChange={(v) => handleChange('color_background', v)}
            />
            <ColorPicker
              label="Fondo del icono"
              value={form.color_icon_background}
              onChange={(v) => handleChange('color_icon_background', v)}
            />
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            El <strong>Fondo del icono</strong> es el color s&oacute;lido detr&aacute;s del logo (útil si el logo es transparente o claro).
            Se usa en el sidebar y, en el futuro, en el &iacute;cono PWA instalado en m&oacute;vil.
          </p>

          {/* Preview */}
          <div className="flex flex-col gap-2">
            <Label>Vista previa</Label>
            <div
              className="rounded-xl p-4 flex items-center gap-3"
              style={{ backgroundColor: form.color_primary }}
            >
              <div className="size-10 rounded-lg flex items-center justify-center overflow-hidden"
                   style={{ backgroundColor: form.color_icon_background }}>
                {(logoPreview || orgLogo) ? (
                  <img src={logoPreview || orgLogo} alt="" className="size-8 object-contain" />
                ) : (
                  <Building2 size={20} style={{ color: form.color_primary }} />
                )}
              </div>
              <div>
                <div className="font-semibold" style={{ color: form.color_background }}>
                  {form.name || 'Mi Organizacion'}
                </div>
                <div className="text-sm" style={{ color: form.color_secondary }}>
                  Control
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Deportes habilitados */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-3">
          <div className="text-foreground font-semibold text-lg">Deportes habilitados</div>
          <div className="flex flex-wrap gap-2">
            {opcionesDeporte(orgDeportes).map((o) => (
              <Badge key={o.value} variant="secondary">{o.label}</Badge>
            ))}
            {orgDeportes.length === 0 && (
              <span className="text-sm text-muted-foreground">Sin deportes asignados</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Los deportes habilitados son gestionados por el administrador de la plataforma.</p>
        </div>


        {/* Info del sistema */}
        <div className="bg-muted rounded-xl border p-6 flex flex-col gap-2">
          <div className="text-muted-foreground font-medium text-sm">Información del sistema</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-muted-foreground">Tipo</div>
            <div className="text-foreground capitalize">{org.type}</div>
            <div className="text-muted-foreground">Slug</div>
            <div className="text-foreground font-mono">{org.slug}</div>
            <div className="text-muted-foreground">ID</div>
            <div className="text-foreground font-mono text-xs truncate">{org.id}</div>
          </div>
        </div>

        {/* Guardar */}
        <div className="flex justify-end">
          <Button size="lg" onClick={handleSave} disabled={saving || !form.name.trim()}>
            <Save data-icon="inline-start" />
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </div>
      </div>
    </FeaturedLayout>
  );
};

export default OrganizationSettings;
